import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';

// ───────────────────────────────────────────────────────────────────────────
// Traffic Analysis — whole-site conversion funnel for crabplace.com (PROD).
//
// Goal (Greg, 2026-07-18): start at "who visited the site today / this range",
// then show how they flow through to a purchase, with drop-off points that
// LEAP OUT so we know where to optimize. Two coupled bands:
//
//   Band A — MACRO (GA4, whole site):
//     Visits (sessions) → Add to Cart → Begin Checkout → Purchase
//
//   Band B — MICRO (checkout telemetry, prod): the granular in-checkout steps
//     Loaded → Shipping → Ship committed → Payment → Place Order → Submitting → Placed
//
// The two bands use different session definitions (GA4 sampled sessions vs raw
// checkout-page telemetry sessions), so they don't nest to the exact number —
// they're stacked + labeled honestly. Directionally they answer "where do we
// lose people, from landing to conversion."
//
// Data: ga4-overview + ga4-funnel passthroughs (→ cp-ga4), and
// checkout-telemetry?action=funnel (→ Neon mirror). All PROD.
// ───────────────────────────────────────────────────────────────────────────

const C = {
  navy: '#003b5c',
  navy2: '#0a4f77',
  navy3: '#1f6f9e',
  orange: '#dc582a',
  peach: '#fcd299',
  green: '#1a8a4a',
  greenBg: '#e6f4ea',
  yellow: '#a06000',
  yellowBg: '#fff4e0',
  red: '#b42318',
  redBg: '#fdeceb',
  ink: '#0f2233',
  muted: '#6b7280',
  line: '#e6e8eb',
  track: '#eef1f4',
};

// ── ET day helpers (Greg + the GA4 property both run America/New_York) ────────
const todayET = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
const isoAddDays = (ymd, n) => { const d = new Date(ymd + 'T12:00:00'); d.setDate(d.getDate() + n); return d.toLocaleDateString('en-CA'); };
const dayStartMs = (d) => new Date(d + 'T00:00:00').getTime();

const PRESETS = [
  { id: 'today',     label: 'Today',        get: () => { const t = todayET(); return [t, t]; } },
  { id: 'yesterday', label: 'Yesterday',    get: () => { const y = isoAddDays(todayET(), -1); return [y, y]; } },
  { id: '7d',        label: 'Last 7 days',  get: () => [isoAddDays(todayET(), -6), todayET()] },
  { id: '30d',       label: 'Last 30 days', get: () => [isoAddDays(todayET(), -29), todayET()] },
  { id: '90d',       label: 'Last 90 days', get: () => [isoAddDays(todayET(), -89), todayET()] },
];

// ── Format helpers ────────────────────────────────────────────────────────────
const nf = (n) => (n == null ? '—' : Number(n).toLocaleString('en-US'));
const pct1 = (x) => (x == null ? '—' : (x * 100).toFixed(1) + '%');
const pct0 = (x) => (x == null ? '—' : Math.round(x * 100) + '%');
const money = (n) => (n == null ? '—' : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }));
const dur = (sec) => {
  if (sec == null) return '—';
  const m = Math.floor(sec / 60), s = Math.round(sec % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

// ── Micro-funnel step model (mirrors CheckoutFunnel's RANK/STEP map) ──────────
// Cumulative "reached at least this stage" over the per-session signal rows.
const MICRO = [
  { key: 'loaded',    label: 'Loaded checkout',   sub: 'Reached the checkout page',           test: (s) => s.maxRank >= 1 },
  { key: 'shipping',  label: 'Reached Shipping',  sub: 'Advanced past customer/login',        test: (s) => s.maxRank >= 2 },
  { key: 'shipCommit',label: 'Shipping committed',sub: 'Picked a rate / date',                test: (s) => s.maxRank >= 3 },
  { key: 'payment',   label: 'Reached Payment',   sub: 'Payment form mounted',                test: (s) => s.maxRank >= 5 },
  { key: 'placeOrder',label: 'Clicked Place Order',sub: 'Hit the Place Order button',         test: (s) => s.maxRank >= 6 || s.clickedPlaceOrder },
  { key: 'submitting',label: 'Submitting to BC',  sub: 'Order POST in flight',                test: (s) => s.maxRank >= 9 || s.submitting },
  { key: 'placed',    label: 'Order placed',      sub: 'Confirmed order',                     test: (s) => s.completed || s.maxRank >= 10 },
];

// Given raw step counts (array of {..., count}), attach funnel math + severity.
// Enforce monotonic non-increasing counts so a noisy signal can't show an uptick.
function withFunnelMath(steps) {
  const top = steps.length ? steps[0].count : 0;
  let prev = null;
  const rows = steps.map((st, i) => {
    let count = st.count;
    if (prev != null) count = Math.min(count, prev);
    const stepConv = prev == null ? 1 : (prev > 0 ? count / prev : null);
    const pctOfTop = top > 0 ? count / top : null;
    const dropCount = prev == null ? 0 : Math.max(0, prev - count);
    const dropPct = prev == null ? null : (prev > 0 ? dropCount / prev : null);
    const row = { ...st, count, stepConv, pctOfTop, dropCount, dropPct, idx: i };
    prev = count;
    return row;
  });
  // Steepest fall-off = lowest step conversion among non-first rows that had a
  // meaningful denominator. That's the most actionable leak (UX, not top-of-funnel).
  let worstIdx = -1, worstConv = 2;
  for (const r of rows) {
    if (r.idx === 0) continue;
    if (r.stepConv == null) continue;
    if (r.stepConv < worstConv) { worstConv = r.stepConv; worstIdx = r.idx; }
  }
  return { rows, worstIdx, top };
}

function convColor(conv) {
  if (conv == null) return { fg: C.muted, bg: '#f3f4f6' };
  if (conv >= 0.8) return { fg: C.green, bg: C.greenBg };
  if (conv >= 0.5) return { fg: C.yellow, bg: C.yellowBg };
  return { fg: C.red, bg: C.redBg };
}

// ── Reference screenshots of the real storefront UI at each micro step ────────
// Captured via .claude/playwright/checkout-stage-shots.js (crabplace.com, never
// places an order) and copied to public/checkout-shots/<key>.png. Drop a new PNG
// at the mapped path and it appears in the drill — no code change. Steps without
// a distinct screen (place-order click, submit-in-flight, confirmation) map to
// their preceding screen or null.
const SHOTS = {
  loaded:     { src: '/checkout-shots/loaded.png',      cap: 'Customer / sign-in step (also a VIP Club upsell gates the way to Shipping)' },
  shipping:   { src: '/checkout-shots/shipping.png',    cap: 'Shipping — address + delivery notes' },
  shipCommit: { src: '/checkout-shots/ship-commit.png', cap: 'Delivery — pick a Shipping Option (rate) to unlock the rest' },
  payment:    { src: '/checkout-shots/payment.png',     cap: 'Payment — card fields + Place Order' },
  placeOrder: { src: '/checkout-shots/payment.png',     cap: 'Payment — the Place Order button lives here' },
  submitting: null,
  placed:     null,
};

// Furthest MICRO step a session reached (highest index whose test passes).
function furthestMicroIdx(s) {
  let far = -1;
  for (let i = 0; i < MICRO.length; i++) if (MICRO[i].test(s)) far = i;
  return far;
}

// Lightweight event-level coloring for the session timeline (no PII in labels).
function levelOf(ev) {
  const e = String(ev || '');
  if (/error|fail|declin|unhandled/i.test(e)) return { t: 'ERR', c: C.red };
  if (/success|\.ok\b|resolved|placed|complete|\.201/i.test(e)) return { t: 'OK', c: C.green };
  if (/blur|hidden|visibility|abandon|warn/i.test(e)) return { t: 'WARN', c: C.yellow };
  return { t: 'INFO', c: C.navy3 };
}

const clockTs = (ts) => (ts ? new Date(ts).toISOString().slice(11, 19) : '');
// Wall-clock elapsed of a session in a human form.
function elapsed(ms) {
  if (ms == null || ms < 0) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return s + 's';
  const m = Math.floor(s / 60), r = s % 60;
  return m + 'm' + (r ? ' ' + r + 's' : '');
}

// ── Small UI atoms ────────────────────────────────────────────────────────────
function Tile({ label, value, sub, accent }) {
  return (
    <div style={{ background: '#fff', border: '1px solid ' + C.line, borderRadius: 12, padding: '13px 15px', borderTop: '3px solid ' + (accent || C.navy), minWidth: 0 }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, color: C.ink, lineHeight: 1.1, marginTop: 4 }}>{value}</div>
      {sub && <div style={{ fontSize: 11.5, color: C.muted, marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

function Chip({ children, fg, bg }) {
  return <span style={{ fontSize: 10.5, fontWeight: 700, color: fg, background: bg, borderRadius: 5, padding: '2px 7px', whiteSpace: 'nowrap' }}>{children}</span>;
}

// ── One funnel band (macro or micro) ─────────────────────────────────────────
function FunnelBand({ title, source, steps, accent, onDrill, selectedKey }) {
  const { rows, worstIdx, top } = useMemo(() => withFunnelMath(steps), [steps]);
  const overall = rows.length ? rows[rows.length - 1].pctOfTop : null;

  return (
    <div style={{ background: '#fff', border: '1px solid ' + C.line, borderRadius: 14, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, padding: '14px 18px', borderBottom: '1px solid ' + C.line, background: '#fbfcfd' }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 800, color: C.navy }}>{title}</div>
          <div style={{ fontSize: 11.5, color: C.muted, marginTop: 1 }}>{source}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: accent }}>{pct1(overall)}</div>
          <div style={{ fontSize: 10.5, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.4 }}>end-to-end</div>
        </div>
      </div>

      <div style={{ padding: '6px 18px 16px' }}>
        {rows.map((r) => {
          const w = top > 0 ? Math.max(2, (r.count / top) * 100) : 0;
          const cc = convColor(r.stepConv);
          const isWorst = r.idx === worstIdx;
          const selected = selectedKey === r.key;
          return (
            <div key={r.key}>
              {/* drop-off connector between rows */}
              {r.idx > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0 2px 4px', height: 18 }}>
                  <span style={{ fontSize: 11, color: r.dropCount > 0 ? C.red : C.muted, fontWeight: 600 }}>
                    ▼ {nf(r.dropCount)} lost{r.dropPct != null ? ` (${pct0(r.dropPct)})` : ''}
                  </span>
                </div>
              )}
              <button
                onClick={() => onDrill && onDrill(r)}
                title="Click to drill into this step"
                style={{
                  width: '100%', textAlign: 'left', cursor: onDrill ? 'pointer' : 'default',
                  display: 'grid', gridTemplateColumns: '190px 1fr 150px', alignItems: 'center', gap: 12,
                  padding: '9px 10px', borderRadius: 10, border: '1px solid ' + (selected ? accent : 'transparent'),
                  background: selected ? '#f7fafc' : (isWorst ? '#fff8f4' : 'transparent'),
                  boxShadow: isWorst ? 'inset 3px 0 0 ' + C.orange : 'none', fontFamily: 'inherit',
                }}>
                {/* label */}
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.ink, display: 'flex', alignItems: 'center', gap: 6 }}>
                    {r.label}
                    {isWorst && <Chip fg={C.orange} bg="#ffe9dd">STEEPEST DROP</Chip>}
                  </div>
                  <div style={{ fontSize: 10.5, color: C.muted, marginTop: 1 }}>{r.sub}</div>
                </div>
                {/* bar — label rides inside (white) when the bar is wide enough,
                    otherwise sits just past the bar end (dark ink on the track) so
                    there's always contrast. */}
                {(() => {
                  const inside = w >= 26;
                  return (
                    <div style={{ position: 'relative', height: 34, background: C.track, borderRadius: 8 }}>
                      <div style={{
                        position: 'absolute', top: 0, bottom: 0, left: 0, width: w + '%',
                        background: `linear-gradient(90deg, ${accent} 0%, ${C.navy3} 100%)`,
                        borderRadius: 8, transition: 'width .5s cubic-bezier(.2,.7,.2,1)',
                      }} />
                      <div style={{ position: 'absolute', top: 0, bottom: 0, display: 'flex', alignItems: 'center', gap: 8,
                        left: inside ? 12 : `calc(${w}% + 10px)`, maxWidth: inside ? 'none' : '70%' }}>
                        <span style={{ fontSize: 15, fontWeight: 800, color: inside ? '#fff' : C.ink }}>{nf(r.count)}</span>
                        <span style={{ fontSize: 11, fontWeight: 600, color: inside ? 'rgba(255,255,255,.9)' : C.muted, whiteSpace: 'nowrap' }}>{pct1(r.pctOfTop)} of visits</span>
                      </div>
                    </div>
                  );
                })()}
                {/* step conversion */}
                <div style={{ textAlign: 'right' }}>
                  {r.idx === 0
                    ? <span style={{ fontSize: 11, color: C.muted }}>entry</span>
                    : <Chip fg={cc.fg} bg={cc.bg}>{pct1(r.stepConv)} continue</Chip>}
                </div>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Drill panel for a selected step ───────────────────────────────────────────
// Micro band: reference screenshot of the real checkout UI at this stage, the
// list of sessions that stalled here, and a per-session event timeline.
// Macro band (GA4): aggregate tiles only — GA4 has no per-session stream.
function DrillPanel({ step, band, shot, stalled, selSid, selEvents, selLoading, expanded, setExpanded, onOpenSession, onClose }) {
  if (!step) return null;
  const cc = convColor(step.stepConv);
  const isMicro = band === 'Inside checkout';
  return (
    <div style={{ background: '#fff', border: '1px solid ' + C.line, borderRadius: 14, padding: 18, borderTop: '3px solid ' + C.orange }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>{band} · step drill-in</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: C.navy, marginTop: 2 }}>{step.label}</div>
          <div style={{ fontSize: 12.5, color: C.muted, marginTop: 2 }}>{step.sub}</div>
        </div>
        <button onClick={onClose} style={{ border: '1px solid ' + C.line, background: '#fff', borderRadius: 8, padding: '5px 10px', cursor: 'pointer', fontSize: 13, color: C.muted }}>✕ Close</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginTop: 14 }}>
        <Tile label="At this step" value={nf(step.count)} sub={`${pct1(step.pctOfTop)} of visits`} accent={C.navy} />
        {step.idx > 0 && <Tile label="Continued from prev" value={pct1(step.stepConv)} accent={cc.fg} />}
        {step.idx > 0 && <Tile label="Lost here" value={nf(step.dropCount)} sub={step.dropPct != null ? pct1(step.dropPct) + ' of prev step' : ''} accent={C.red} />}
        {isMicro && stalled && <Tile label={stalled.isLast ? 'Placed order' : 'Stalled here'} value={nf(stalled.rows.length)} sub={stalled.isLast ? 'reached the end' : 'went no further'} accent={stalled.isLast ? C.green : C.orange} />}
      </div>

      {/* Screenshot of the exact UI they saw at this step */}
      {isMicro && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: C.navy, marginBottom: 6 }}>📸 What shoppers saw here</div>
          {shot
            ? <StepShot shot={shot} />
            : <div style={{ background: '#f7fafc', border: '1px dashed ' + C.line, borderRadius: 10, padding: '12px 14px', fontSize: 12, color: C.muted }}>
                No distinct screen for this step — it’s a button press / in-flight moment, not a separate page.
              </div>}
        </div>
      )}

      {/* Sessions that stalled at this step → click for their timeline */}
      {isMicro && stalled && (
        <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: selSid ? 'minmax(0, 360px) 1fr' : '1fr', gap: 14 }}>
          <div>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: C.navy, marginBottom: 6 }}>
              {stalled.isLast ? 'Sessions that placed' : 'Sessions that stalled here'}
              <span style={{ color: C.muted, fontWeight: 600 }}> · {nf(stalled.rows.length)}</span>
            </div>
            {stalled.rows.length === 0
              ? <div style={{ fontSize: 12, color: C.muted, padding: '8px 0' }}>None in this range.</div>
              : <div style={{ maxHeight: '52vh', overflowY: 'auto', border: '1px solid ' + C.line, borderRadius: 10 }}>
                  {stalled.rows.slice(0, 200).map((s) => (
                    <StalledRow key={s.sessionId} s={s} active={selSid === s.sessionId} onClick={() => onOpenSession(s.sessionId)} />
                  ))}
                  {stalled.rows.length > 200 && <div style={{ fontSize: 11, color: C.muted, padding: '8px 10px' }}>+{nf(stalled.rows.length - 200)} more…</div>}
                </div>}
          </div>
          {selSid && <SessionTimeline selSid={selSid} selEvents={selEvents} selLoading={selLoading} expanded={expanded} setExpanded={setExpanded} />}
        </div>
      )}

      {!isMicro && (
        <div style={{ marginTop: 14, background: '#f7fafc', border: '1px dashed ' + C.line, borderRadius: 10, padding: '12px 14px', fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
          This is a whole-site GA4 step, so there’s no per-session stream to open. Drill into the <strong>Inside checkout</strong> band below to see the exact screens and the sessions that stalled at each stage.
        </div>
      )}
    </div>
  );
}

// Reference screenshot with click-to-zoom (full image in a lightbox).
function StepShot({ shot }) {
  const [zoom, setZoom] = useState(false);
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div style={{ background: '#f7fafc', border: '1px dashed ' + C.line, borderRadius: 10, padding: '12px 14px', fontSize: 12, color: C.muted }}>
        Screenshot for this step hasn’t been captured yet — drop a PNG at <code style={{ fontFamily: 'monospace' }}>public{shot.src}</code>.
      </div>
    );
  }
  return (
    <>
      <button onClick={() => setZoom(true)} title="Click to enlarge"
        style={{ display: 'block', width: '100%', padding: 0, border: '1px solid ' + C.line, borderRadius: 10, overflow: 'hidden', cursor: 'zoom-in', background: '#fbfcfd' }}>
        <img src={shot.src} alt={shot.cap} onError={() => setFailed(true)}
          style={{ display: 'block', width: '100%', maxHeight: 340, objectFit: 'cover', objectPosition: 'top' }} />
        <div style={{ fontSize: 11.5, color: C.muted, textAlign: 'left', padding: '7px 11px', background: '#fff', borderTop: '1px solid ' + C.line }}>{shot.cap} · click to enlarge</div>
      </button>
      {zoom && (
        <div onClick={() => setZoom(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,34,51,.82)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, cursor: 'zoom-out' }}>
          <img src={shot.src} alt={shot.cap} style={{ maxWidth: '96vw', maxHeight: '92vh', borderRadius: 8, boxShadow: '0 12px 40px rgba(0,0,0,.5)' }} />
        </div>
      )}
    </>
  );
}

// One stalled-session row.
function StalledRow({ s, active, onClick }) {
  const dwell = s.lastTs && s.firstTs ? s.lastTs - s.firstTs : null;
  const tag = s.hasErrorLevel ? { t: 'error', c: C.red } : s.hasFriction ? { t: 'friction', c: C.yellow } : s.hasExternal ? { t: 'external', c: C.navy3 } : null;
  return (
    <button onClick={onClick}
      style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', cursor: 'pointer', fontFamily: 'inherit',
        border: 'none', borderBottom: '1px solid ' + C.line, background: active ? '#eef4fb' : 'transparent', fontSize: 12 }}>
      <span style={{ fontFamily: 'monospace', color: C.ink, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{String(s.sessionId).slice(0, 20)}</span>
      {tag && <Chip fg={tag.c} bg="#f3f4f6">{tag.t}</Chip>}
      <span style={{ color: C.muted, width: 46, textAlign: 'right', flexShrink: 0 }}>{s.eventCount}ev</span>
      <span style={{ color: C.muted, width: 56, textAlign: 'right', flexShrink: 0 }}>{elapsed(dwell)}</span>
    </button>
  );
}

// Per-session event timeline — the raw stream, newest last, data expandable.
function SessionTimeline({ selSid, selEvents, selLoading, expanded, setExpanded }) {
  const t0 = selEvents.length ? selEvents[0].timestamp : null;
  return (
    <div style={{ border: '1px solid ' + C.line, borderRadius: 10, padding: '12px 14px', minWidth: 0 }}>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: C.navy }}>
        Session timeline {selLoading && <span style={{ fontSize: 11, color: C.muted, fontWeight: 600 }}>· loading…</span>}
      </div>
      <div style={{ fontSize: 10.5, color: '#9ca3af', margin: '2px 0 8px', fontFamily: 'monospace' }}>
        {selSid}{selEvents.length ? ' · ' + clockTs(t0) + ' UTC · ' + elapsed(selEvents[selEvents.length - 1].timestamp - t0) : ''}
      </div>
      {!selLoading && selEvents.length === 0 && <div style={{ fontSize: 12, color: C.muted }}>No events.</div>}
      <div style={{ maxHeight: '52vh', overflowY: 'auto' }}>
        {selEvents.map((e, i) => {
          const lv = levelOf(e.event);
          const isOpen = !!expanded[i];
          const hasData = e.data && Object.keys(e.data).length > 0;
          const rel = t0 != null ? '+' + elapsed(e.timestamp - t0) : '';
          return (
            <div key={i} style={{ borderBottom: '1px solid #f1f3f5', padding: '5px 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                <span style={{ width: 44, textAlign: 'center', color: '#fff', background: lv.c, borderRadius: 4, fontSize: 9, padding: '2px 0', fontWeight: 700, flexShrink: 0 }}>{lv.t}</span>
                <span style={{ fontFamily: 'monospace', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.event}</span>
                <span style={{ color: '#9ca3af', fontFamily: 'monospace', fontSize: 10.5, flexShrink: 0 }}>{rel}</span>
                {hasData && (
                  <button onClick={() => setExpanded(p => ({ ...p, [i]: !p[i] }))}
                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: C.muted, fontSize: 11, flexShrink: 0 }}>{isOpen ? '▼' : '▶'}</button>
                )}
              </div>
              {isOpen && hasData && (
                <pre style={{ background: '#0d1117', color: '#c9d1d9', fontSize: 10.5, padding: 9, borderRadius: 6, overflowX: 'auto', marginTop: 5 }}>
                  {JSON.stringify(e.data, null, 2)}
                </pre>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Tabs (Funnel is live; the rest are the roadmap Greg sketched) ─────────────
const TABS = [
  { id: 'funnel', label: 'Funnel', live: true },
  { id: 'engagement', label: 'Engagement', live: false },
  { id: 'acquisition', label: 'Acquisition', live: false },
  { id: 'behavior', label: 'On-site behavior', live: false },
];

export default function TrafficAnalysis() {
  const [tab, setTab] = useState('funnel');
  // Default to Yesterday — a complete day of data, and it matches the other
  // analytics pages' default. (Today is a partial, still-running day.)
  const [startIso, setStartIso] = useState(() => isoAddDays(todayET(), -1));
  const [endIso, setEndIso] = useState(() => isoAddDays(todayET(), -1));

  const [overview, setOverview] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [micro, setMicro] = useState(null); // { steps:[{...count}], scanned }
  const [loading, setLoading] = useState(false);
  const [errs, setErrs] = useState({});
  const [loadedAt, setLoadedAt] = useState(null);
  const [drill, setDrill] = useState(null); // { step, band }

  // Selected stalled session → its full event stream (the timeline).
  const [selSid, setSelSid] = useState(null);
  const [selEvents, setSelEvents] = useState([]);
  const [selLoading, setSelLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  const reqRef = useRef(0);
  const selReqRef = useRef(0);

  // Fetch one session's full event stream from the telemetry mirror.
  const openSession = useCallback(async (sid) => {
    const my = ++selReqRef.current;
    setSelSid(sid); setSelEvents([]); setSelLoading(true); setExpanded({});
    const sinceMs = dayStartMs(startIso);
    const untilMs = Math.min(dayStartMs(endIso) + 864e5, Date.now());
    try {
      const qs = new URLSearchParams({ env: 'prod', sessionId: sid, since: String(sinceMs), until: String(untilMs), limit: '1000' });
      const res = await fetch('/.netlify/functions/checkout-telemetry?' + qs.toString());
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || ('session fetch failed (' + res.status + ')'));
      if (my !== selReqRef.current) return;
      setSelEvents((body.events || []).slice().sort((a, b) => a.timestamp - b.timestamp));
    } catch (e) {
      if (my !== selReqRef.current) return;
      setSelEvents([{ event: '(load failed)', timestamp: Date.now(), data: { error: e.message } }]);
    } finally {
      if (my === selReqRef.current) setSelLoading(false);
    }
  }, [startIso, endIso]);

  const load = useCallback(async () => {
    const myReq = ++reqRef.current;
    setLoading(true); setErrs({}); setDrill(null); setSelSid(null); setSelEvents([]);
    const sinceMs = dayStartMs(startIso);
    const untilMs = Math.min(dayStartMs(endIso) + 864e5, Date.now());
    const e = {};

    const pOverview = fetch(`/.netlify/functions/ga4-overview?start=${startIso}&end=${endIso}`)
      .then(r => r.json()).then(d => { if (d.error) throw new Error(d.error); return d; })
      .catch(err => { e.overview = err.message; return null; });
    const pFunnel = fetch(`/.netlify/functions/ga4-funnel?startDate=${startIso}&endDate=${endIso}`)
      .then(r => r.json()).then(d => { if (d.error) throw new Error(d.error); return d; })
      .catch(err => { e.funnel = err.message; return null; });
    const pMicro = fetch(`/.netlify/functions/checkout-telemetry?action=funnel&env=prod&since=${sinceMs}&until=${untilMs}`)
      .then(r => r.json()).then(d => {
        if (d.error) throw new Error(d.error);
        const sessions = d.sessions || [];
        const steps = MICRO.map(m => ({ key: m.key, label: m.label, sub: m.sub, count: sessions.filter(m.test).length }));
        return { steps, scanned: sessions.length, sessions };
      })
      .catch(err => { e.micro = err.message; return null; });

    const [ov, fn, mi] = await Promise.all([pOverview, pFunnel, pMicro]);
    if (myReq !== reqRef.current) return;
    setOverview(ov); setFunnel(fn); setMicro(mi); setErrs(e);
    setLoadedAt(Date.now()); setLoading(false);
  }, [startIso, endIso]);

  useEffect(() => { load(); }, [load]);

  const macroSteps = useMemo(() => {
    if (!funnel) return [];
    return [
      { key: 'sessions', label: 'Visits',         sub: 'GA4 sessions',          count: funnel.sessions },
      { key: 'atc',      label: 'Add to Cart',    sub: 'add_to_cart events',    count: funnel.add_to_carts },
      { key: 'checkout', label: 'Begin Checkout', sub: 'begin_checkout events', count: funnel.checkouts },
      { key: 'purchase', label: 'Purchase',       sub: 'completed transactions', count: funnel.transactions },
    ];
  }, [funnel]);

  const returning = overview ? Math.max(0, (overview.active_users || 0) - (overview.new_users || 0)) : null;
  const rangeLabel = startIso === endIso ? startIso : `${startIso} → ${endIso}`;

  // Sessions that stalled AT the drilled micro step (furthest reached == this
  // step, and didn't complete). The terminal step lists the sessions that placed.
  const stalled = useMemo(() => {
    if (!drill || drill.band !== 'Inside checkout' || !micro?.sessions) return null;
    const idx = MICRO.findIndex(m => m.key === drill.step.key);
    if (idx < 0) return null;
    const isLast = idx === MICRO.length - 1;
    const rows = micro.sessions.filter(s => (s.completed ? isLast : furthestMicroIdx(s) === idx));
    rows.sort((a, b) => (b.lastTs - b.firstTs) - (a.lastTs - a.firstTs));
    return { idx, isLast, rows };
  }, [drill, micro]);

  const navy = C.navy, orange = C.orange;
  const inputStyle = { padding: '6px 9px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13, fontFamily: 'inherit' };

  return (
    <div style={{ padding: 22, fontFamily: 'Montserrat, sans-serif', maxWidth: 1200, margin: '0 auto' }}>
      {/* Header + range controls */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
        <div>
          <h1 style={{ margin: 0, color: navy, fontSize: 25, fontWeight: 800 }}>Traffic Analysis</h1>
          <div style={{ color: C.muted, fontSize: 13, marginTop: 3 }}>
            From landing on crabplace.com to a placed order — where shoppers flow and where they fall off.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', border: '1px solid ' + navy, borderRadius: 8, overflow: 'hidden' }}>
            {PRESETS.map(p => {
              const [ps, pe] = p.get();
              const active = startIso === ps && endIso === pe;
              return (
                <button key={p.id} onClick={() => { setStartIso(ps); setEndIso(pe); }}
                  style={{ padding: '6px 11px', border: 'none', cursor: 'pointer', fontSize: 12.5, fontWeight: 600,
                    background: active ? orange : '#fff', color: active ? '#fff' : navy }}>
                  {p.label}
                </button>
              );
            })}
          </div>
          <input type="date" value={startIso} max={endIso} onChange={e => setStartIso(e.target.value)} style={inputStyle} />
          <span style={{ color: C.muted, fontSize: 13 }}>to</span>
          <input type="date" value={endIso} min={startIso} max={todayET()} onChange={e => setEndIso(e.target.value)} style={inputStyle} />
          <button onClick={load} disabled={loading}
            style={{ padding: '6px 14px', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
              background: navy, color: '#fff', opacity: loading ? 0.6 : 1 }}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 18, borderBottom: '1px solid ' + C.line }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => t.live && setTab(t.id)} disabled={!t.live}
            style={{ padding: '9px 15px', border: 'none', borderBottom: '3px solid ' + (tab === t.id ? orange : 'transparent'),
              background: 'none', cursor: t.live ? 'pointer' : 'default', fontSize: 13.5,
              fontWeight: tab === t.id ? 800 : 600, color: t.live ? (tab === t.id ? navy : C.muted) : '#c2c8cf', fontFamily: 'inherit' }}>
            {t.label}{!t.live && <span style={{ fontSize: 9.5, marginLeft: 5, verticalAlign: 'middle', color: '#c2c8cf', border: '1px solid #dfe3e8', borderRadius: 4, padding: '1px 4px' }}>SOON</span>}
          </button>
        ))}
      </div>

      {tab === 'funnel' && (
        <>
          {/* Context tiles */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 18 }}>
            <Tile label="Visits (sessions)" value={nf(overview?.sessions ?? funnel?.sessions)} sub={overview ? `${nf(overview.pageviews)} pageviews` : ''} accent={C.navy} />
            <Tile label="Users" value={nf(overview?.active_users)} sub={overview ? `${nf(overview.new_users)} new · ${nf(returning)} returning` : ''} accent={C.navy2} />
            <Tile label="Conversion rate" value={overview ? overview.conversion_rate + '%' : '—'} sub="session → purchase" accent={C.green} />
            <Tile label="Revenue" value={money(overview?.revenue)} sub={overview ? `${nf(overview.transactions)} orders` : ''} accent={C.orange} />
            <Tile label="Avg order value" value={money(overview?.avg_order_value)} accent={C.orange} />
            <Tile label="Avg session" value={dur(overview?.avg_session_duration_sec)} sub={overview ? `${overview.bounce_rate}% bounce` : ''} accent={C.navy3} />
          </div>

          {/* status line */}
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 14 }}>
            {loading && <span>Loading GA4 + checkout telemetry for {rangeLabel}…</span>}
            {!loading && loadedAt && (
              <span>
                Range <strong>{rangeLabel}</strong>
                {micro && <> · {nf(micro.scanned)} checkout sessions scanned</>}
                {Object.keys(errs).length > 0 && <span style={{ color: C.red }}> · ⚠ {Object.entries(errs).map(([k, v]) => `${k}: ${v}`).join('; ')}</span>}
              </span>
            )}
          </div>

          {/* Funnel bands */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {funnel
              ? <FunnelBand title="Site funnel" source="GA4 · whole site (sampled sessions)" steps={macroSteps} accent={C.navy}
                  selectedKey={drill?.band === 'Site funnel' ? drill.step.key : null}
                  onDrill={(step) => { setDrill({ step, band: 'Site funnel' }); setSelSid(null); setSelEvents([]); }} />
              : !loading && <BandError label="Site funnel (GA4)" msg={errs.funnel} />}

            {micro && micro.steps[0]?.count > 0
              ? <FunnelBand title="Inside checkout" source="Checkout telemetry · prod (raw sessions that reached /checkout)" steps={micro.steps} accent={C.navy2}
                  selectedKey={drill?.band === 'Inside checkout' ? drill.step.key : null}
                  onDrill={(step) => { setDrill({ step, band: 'Inside checkout' }); setSelSid(null); setSelEvents([]); }} />
              : micro && !loading
                ? <div style={{ background: '#fff', border: '1px solid ' + C.line, borderRadius: 14, padding: 18, fontSize: 13, color: C.muted }}>No checkout sessions reached /checkout in this range.</div>
                : (!micro && !loading && <BandError label="Inside checkout (telemetry)" msg={errs.micro} />)}
          </div>

          {/* Drill panel */}
          {drill && <div style={{ marginTop: 16 }}>
            <DrillPanel step={drill.step} band={drill.band}
              shot={SHOTS[drill.step.key]} stalled={stalled}
              selSid={selSid} selEvents={selEvents} selLoading={selLoading}
              expanded={expanded} setExpanded={setExpanded}
              onOpenSession={openSession} onClose={() => setDrill(null)} />
          </div>}

          {/* How to read */}
          <div style={{ marginTop: 18, fontSize: 11.5, color: C.muted, lineHeight: 1.6, background: '#fbfcfd', border: '1px solid ' + C.line, borderRadius: 10, padding: '12px 15px' }}>
            <strong style={{ color: C.navy }}>How to read this.</strong> The <em>Site funnel</em> is GA4's view of everyone on crabplace.com. The <em>Inside checkout</em> band zooms into the checkout page itself using our own telemetry, so its "Loaded checkout" count and GA4's "Begin Checkout" won't match to the exact number — they count sessions differently. Each bar shows how many made it and what share of visits that is; the chip on the right is how many of the <em>previous</em> step continued. The <span style={{ color: C.orange, fontWeight: 700 }}>steepest-drop</span> row in each band is your biggest optimization opportunity. Click any step to drill in.
          </div>
        </>
      )}
    </div>
  );
}

function BandError({ label, msg }) {
  return (
    <div style={{ background: '#fff', border: '1px solid ' + C.line, borderRadius: 14, padding: 18 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: C.navy }}>{label}</div>
      <div style={{ fontSize: 12.5, color: C.red, marginTop: 4 }}>Couldn't load{msg ? `: ${msg}` : '.'}</div>
    </div>
  );
}
